import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_STEPS } from '../utils/onboardingSteps';
import { OnboardingChecklistItem } from './OnboardingChecklistItem';
import type { OnboardingStatusResponse } from '../api/onboardingApi';
import type { OnboardingStepKey } from '../utils/onboardingSteps';

const CHECKLIST_DESCRIPTIONS: Record<OnboardingStepKey, string> = {
  invite:
    'Add your workers so they can book work on your sites and get paid via the mobile app.',
  'import-site':
    'Sites represent your construction projects. Import one to start tracking plots, labour items and costs.',
  'assign-sites':
    'Control which workers can see and book work on each site by assigning them.',
  'labour-groups':
    'Labour groups organise your trade categories (e.g. Brickwork, Joinery) so workers see the right items to book.',
  'assign-groups':
    'Link each worker to their trade group so they see the right labour items when booking.',
};

const CHECKLIST_TEST_IDS: Record<OnboardingStepKey, string> = {
  invite: 'onboarding-step-invite',
  'import-site': 'onboarding-step-site',
  'assign-sites': 'onboarding-step-site-assign',
  'labour-groups': 'onboarding-step-labour',
  'assign-groups': 'onboarding-step-assign',
};

function getStatusText(
  key: OnboardingStepKey,
  status: OnboardingStatusResponse
): string {
  switch (key) {
    case 'invite': {
      const isComplete = status.invitedUserCount >= 1 || status.userCount > 1;
      if (!isComplete) {
        return 'No users yet';
      }
      const count = status.invitedUserCount + status.userCount - 1;
      return `${count} user${count === 1 ? '' : 's'} invited`;
    }
    case 'import-site': {
      if (status.siteCount === 0) {
        return 'No sites yet';
      }
      return `${status.siteCount} site${status.siteCount === 1 ? '' : 's'} imported`;
    }
    case 'assign-sites': {
      if (status.siteAssignmentCount === 0) {
        return 'No assignments yet';
      }
      return `${status.siteAssignmentCount} assignment${status.siteAssignmentCount === 1 ? '' : 's'}`;
    }
    case 'labour-groups': {
      if (status.labourGroupCount === 0) {
        return 'No groups yet';
      }
      return `${status.labourGroupCount} group${status.labourGroupCount === 1 ? '' : 's'} created`;
    }
    case 'assign-groups': {
      if (status.assignmentCount === 0) {
        return 'None assigned';
      }
      return `${status.assignmentCount} assigned`;
    }
  }
}

interface OnboardingChecklistProps {
  status: OnboardingStatusResponse;
}

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  status,
}) => {
  const navigate = useNavigate();

  return (
    <div data-testid="onboarding-checklist">
      <style>{`
        @keyframes gsob-fade-slide-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {ONBOARDING_STEPS.map((step, index) => (
        <OnboardingChecklistItem
          key={CHECKLIST_TEST_IDS[step.key]}
          step={index + 1}
          title={step.title}
          description={CHECKLIST_DESCRIPTIONS[step.key]}
          icon={step.icon}
          statusText={getStatusText(step.key, status)}
          isComplete={step.isComplete(status)}
          ctaLabel={step.ctaLabel}
          onCtaClick={() => navigate(step.ctaPath)}
          isLast={index === ONBOARDING_STEPS.length - 1}
          data-testid={CHECKLIST_TEST_IDS[step.key]}
        />
      ))}
    </div>
  );
};
